import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function ContactCta() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="contact-cta" ref={ref} className={cn("py-16 md:py-24 bg-background transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-4">
        <div className="bg-card rounded-[24px] p-8 md:p-12 lg:px-16 shadow-2xl max-w-5xl mx-auto overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <h2 className="text-2xl md:text-3xl font-bold text-base mb-4 leading-tight">
                Questions about our terms?
              </h2>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                We're here to help clarify any details. Fill out the form and
                we'll get back to you as soon as possible.
              </p>
            </div>

            <div className="lg:col-span-8 w-full">
              <FormProvider formId="contact_form_termsandconditions" sectionName="contact-cta" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: false
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you! We've received your inquiry.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="h-full flex flex-col items-center justify-center p-8 bg-base/5 rounded-xl border border-border text-center animate-in fade-in zoom-in duration-500">
        <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <Send className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-base mb-2">Message Sent</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base/90">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-base border-transparent text-base placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/50 h-11 transition-all duration-300" />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base/90">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-base border-transparent text-base placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/50 h-11 transition-all duration-300" />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base/90">
              Phone Number <span className="text-muted-foreground text-xs ml-1">(Optional)</span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+61 2 1234 5678" {...field} className="bg-base border-transparent text-base placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/50 h-11 transition-all duration-300" />
            </FormControl>
            <FormDescription className="text-muted-foreground text-xs">
              Include your phone for quicker contact.
            </FormDescription>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base/90">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your project or inquiry..." {...field} className="bg-base border-transparent text-base placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/50 min-h-[120px] resize-none transition-all duration-300" />
            </FormControl>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={isSubmitting} size="sm" className="rounded-full btn-primary px-8 h-10 font-medium transition-all duration-300">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-4" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}